import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { selectableProviderTypesFor } from 'components/preferredProviders/helpers';
import SharedPreferredProvidersSlideout from 'components/preferredProviders/PreferredProvidersSlideout';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { adminClientsQueryKeys, adminShowClient } from 'services/api/admin/clients';
import { adminLocationsQueryKeys, adminShowLocation } from 'services/api/admin/locations';
import { useToastActions } from 'stores/toastStore';

// The response carries both lists as GroupTypeFields (services/api/fields/admin/client.ts), but the flexible
// Client model types its nested GroupTypes without fields, so read them as what they are.
const providerTypesOf = (client: object) =>
  client as { managedProviderTypes: { id: string }[]; providedProviderTypes: { id: string }[] };

type Props = {
  locationId: string;
  locationName: string;
  clientId: string;
  handleClose: () => void;
};

// Admin Locations' "Manage preferred providers": the customer page's slideout for any location. The admin
// isn't acting as the location's client, so this loads the location's list and the client's provider types
// itself, with the panel open and loading meanwhile. Own keys, since both shows have other includes elsewhere.
export default function PreferredProvidersSlideout({ locationId, locationName, clientId, handleClose }: Props) {
  const { profile } = useProfile();
  const { addToast } = useToastActions();

  const { data: location, isError: locationFailed } = useQuery({
    queryKey: adminLocationsQueryKeys.preferredProviderList(locationId),
    queryFn: ({ signal }) => adminShowLocation(locationId, signal, ['preferredProviderList']),
  });
  const { data: client, isError: clientFailed } = useQuery({
    queryKey: [...adminClientsQueryKeys.show(clientId), 'providerTypes'],
    queryFn: ({ signal }) => adminShowClient(clientId, signal, ['managedProviderTypes', 'providedProviderTypes']),
  });

  // Queries don't retry, so a failed load would otherwise spin forever. Close with a toast, like
  // HighCostMedListSlideout drops a failed load, rather than open read-only: without the saved list, Apply
  // would replace it.
  const failed = locationFailed || clientFailed;
  useEffect(() => {
    if (!failed) return;

    addToast({ text: "This location's preferred providers couldn't be loaded. Please try again." });
    handleClose();
  }, [failed, addToast, handleClose]);

  const list = location?.preferredProviderList;

  return (
    <SharedPreferredProvidersSlideout
      row={{
        locationId,
        locationName,
        updatedAt: list?.updatedAt ?? null,
        updatedByName: list?.updatedByName ?? null,
        preferredProviders: list?.preferredProviders ?? [],
      }}
      clientId={clientId}
      selectableTypeIds={client ? selectableProviderTypesFor(providerTypesOf(client)).map((type) => type.id) : []}
      canEdit={profile.has(PERMISSIONS.adminGroupEdit)}
      admin
      loading={!location || !client}
      handleClose={handleClose}
    />
  );
}
